import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addNewStockEmployee, fetchStockEmployeeByName, fetchStockEmployees, updateExistingStockEmployee } from '../../services/stockEmployeeService';
import { StockEmployee } from '../../models';

export interface StockEmployeesState {
  employees: StockEmployee[];
  totalRow: number;
  isEmployeesLoading: boolean;
  getEmployeesError: string | null;
  isAddEmployeeLoading: boolean;
  addEmployeeError?: string | null;
  selectedEmployeeToUpdate?: StockEmployee | null;
  getEmployeeByNameError?: string | null;
  isUpdateEmployeeLoading: boolean;
  updateEmployeeError?: string | null;
  isGetEmployeeByNameLoading: boolean;
}

const initialState: StockEmployeesState = {
  employees: [],
  isEmployeesLoading: false,
  totalRow: 0,
  getEmployeesError: null,
  isAddEmployeeLoading: false,
  selectedEmployeeToUpdate: null,
  getEmployeeByNameError: null,
  isUpdateEmployeeLoading: false,
  isGetEmployeeByNameLoading: false,
};

export const getStockEmployees = createAsyncThunk('stockEmployees/table', async (payload: any, thunkAPI) => {
  try {
    const res = await fetchStockEmployees(payload);
    return res;
  } catch (error: unknown) {
    return thunkAPI.rejectWithValue('Failed to fetch stock employees');
  }
});

export const getStockEmployeeByName = createAsyncThunk('stockEmployees/getByName', async (name: string, thunkAPI) => {
  try {
    const response = await fetchStockEmployeeByName(name);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch stock employee');
  }
});

export const addStockEmployee = createAsyncThunk('stockEmployees/add', async (employee: StockEmployee, thunkAPI) => {
  try {
    const response = await addNewStockEmployee(employee);
    return response.data;
  } catch (error: any) {
    const detail = error.response?.data?.detail;
    return thunkAPI.rejectWithValue(detail || 'Не удалось добавить сотрудника');
  }
});

export const updateStockEmployee = createAsyncThunk(
  'stockEmployees/update',
  async ({ name, employee }: { name: string; employee: StockEmployee }, thunkAPI) => {
    try {
      const response = await updateExistingStockEmployee({ ...employee, name });
      return response.data;
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      return thunkAPI.rejectWithValue(detail || 'Не удалось обновить сотрудника');
    }
  }
);

const stockEmployeesSlice = createSlice({
  name: 'stockEmployees',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStockEmployees.pending, (state) => {
        state.isEmployeesLoading = true;
      })
      .addCase(getStockEmployees.fulfilled, (state, action) => {
        const res = action.payload;
        state.isEmployeesLoading = false;
        state.employees = res.rows;
        state.totalRow = res?.totalRows;
      })
      .addCase(getStockEmployees.rejected, (state, action) => {
        state.isEmployeesLoading = false;
        state.getEmployeesError = action.payload as string;
      })

      .addCase(getStockEmployeeByName.pending, (state) => {
        state.isGetEmployeeByNameLoading = true;
      })
      .addCase(getStockEmployeeByName.fulfilled, (state, action) => {
        state.isGetEmployeeByNameLoading = false;
        state.selectedEmployeeToUpdate = action.payload;
      })
      .addCase(getStockEmployeeByName.rejected, (state, action) => {
        state.isGetEmployeeByNameLoading = false;
        state.getEmployeeByNameError = action.payload as string;
      })

      .addCase(addStockEmployee.pending, (state) => {
        state.isAddEmployeeLoading = true;
      })
      .addCase(addStockEmployee.fulfilled, (state) => {
        state.isAddEmployeeLoading = false;
      })
      .addCase(addStockEmployee.rejected, (state, action) => {
        state.isAddEmployeeLoading = false;
        state.addEmployeeError = action.payload as string;
      })

      .addCase(updateStockEmployee.pending, (state) => {
        state.isUpdateEmployeeLoading = true;
      })
      .addCase(updateStockEmployee.fulfilled, (state) => {
        state.isUpdateEmployeeLoading = false;
      })
      .addCase(updateStockEmployee.rejected, (state, action) => {
        state.isUpdateEmployeeLoading = false;
        state.updateEmployeeError = action.payload as string;
      });
  },
});

export default stockEmployeesSlice.reducer;
